import { useState } from "react";
import { Box, Button, Chip, Stack, TextField } from "@mui/material";
import { DATE_PRESETS, isoToday, isoPlusDays } from "./datePresets";

/**
 * Chip row of date presets plus a "Custom" chip that reveals From/To fields.
 * Picking a preset calls onApply immediately; Custom waits for the button.
 *
 * Props
 *   presetIds   array of keys from DATE_PRESETS, shown in this order
 *   onApply     (label, startDate, endDate) => void     (dates are YYYY-MM-DD)
 *   applyLabel  text on the custom-range button ("Search", "Generate"...)
 *   accentColor highlight colour for the chosen chip
 */
export default function PeriodPicker({ presetIds, onApply, applyLabel = "Apply", accentColor = "#2563eb" }) {
  const [custom, setCustom] = useState(false);
  const [picked, setPicked] = useState(null);
  const [start, setStart] = useState(isoToday);
  const [end, setEnd] = useState(() => isoPlusDays(7));
  const invalid = !start || !end || end < start;

  const apply = (label, a, b) => {
    setPicked(label);
    onApply(label, a, b);
  };

  const chipSx = (active, dashed) => ({
    height: 28,
    fontSize: 12,
    fontWeight: 600,
    bgcolor: active ? accentColor : "#fff",
    color: active ? "#fff" : "#0f172a",
    border: `1px ${dashed ? "dashed" : "solid"} ${active ? accentColor : "#e2e8f0"}`,
    transition: "all 150ms ease",
    "&:hover": { bgcolor: active ? accentColor : "#eff6ff", borderColor: accentColor },
  });

  const dateFieldSx = { flex: 1, minWidth: 0, "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8fafc" } };

  return (
    <Box sx={{ boxSizing: "border-box", width: "100%" }}>
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {presetIds.map((id) => {
          const p = DATE_PRESETS[id];
          if (!p) return null;
          return (
            <Chip
              key={id}
              label={p.label}
              size="small"
              onClick={() => {
                setCustom(false);
                const [a, b] = p.range();
                apply(p.label, a, b);
              }}
              sx={chipSx(picked === p.label && !custom)}
            />
          );
        })}
        <Chip
          label="Custom"
          size="small"
          onClick={() => setCustom(true)}
          sx={chipSx(custom, !custom)}
        />
      </Stack>

      {custom && (
        <Box sx={{ mt: 1 }}>
          <Stack direction="row" gap={1}>
            <TextField
              type="date"
              size="small"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              inputProps={{ "aria-label": "From date", style: { fontSize: 13, padding: "6px 10px" } }}
              sx={dateFieldSx}
            />
            <TextField
              type="date"
              size="small"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              inputProps={{ "aria-label": "To date", min: start, style: { fontSize: 13, padding: "6px 10px" } }}
              sx={dateFieldSx}
            />
          </Stack>
          <Button
            fullWidth
            size="small"
            variant="contained"
            disableElevation
            disabled={invalid}
            onClick={() => apply("Custom", start, end)}
            sx={{ mt: 1, textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 2 }}
          >
            {applyLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
}
